import React, { Component } from "react";
import styled from "styled-components/macro";
import TextQuestion from "../../components/TextQuestion";
import ScrollToTop from "../../components/utils/ScrollToTop";
import Card from "../../components/Card";
import Action from "../../components/Action";
import List, { ListItem } from "../../components/List";
import Link from "../../components/Link";
import TrustpilotBanner from "../../components/TrustpilotBanner";
import { isEmailValid } from "../../shared/utils/validation";
import { isPhoneNumberValid } from "../../shared/utils/numberUtils";

class SaveQuoteForm extends Component {
  constructor(props) {
    super(props);

    if (this.props.personalDetails) {
      const details = this.props.personalDetails;
      this.state = {
        firstName: details.firstName,
        lastName: details.lastName,
        emailAddress: details.emailAddress,
        phoneNumber: details.phoneNumber,
        errors: {},
        quoteDetails: null,
        loading: true,
        error: null,
      };
    } else {
      this.state = {
        firstName: "",
        lastName: "",
        emailAddress: "",
        phoneNumber: "",
        errors: {},
        quoteDetails: null,
        loading: true,
        error: null,
      };
    }
  }

  handleFetchError = (err) => {
    console.error("Quote generation failed:", err);
    this.setState({ error: "Unable to generate your quote", loading: false });
  };

  componentDidMount() {
    if (this.hasInitiated) return;
    this.hasInitiated = true;

    let channel = this.props.defaultChannel;
    if (this.props.referral?.referralSource) {
      channel = this.props.referral.referralSource;
    }

    fetch("/api/partialQuote", {
      method: "PUT",
      body: JSON.stringify({
        channel: channel,
        sellingDetails:
          this.props.transactionType === "Selling" || this.props.transactionType === "BuyingAndSelling"
            ? this.props.sellingDetails
            : null,
        buyingDetails:
          this.props.transactionType === "Buying" || this.props.transactionType === "BuyingAndSelling"
            ? this.props.buyingDetails
            : null,
        remortgagingDetails: null,
        referralCode: this.props.referral ? this.props.referral.referralCode : null,
        googleAnalyticsId: this.props.googleAnalyticsId,
        campaign: this.props.campaign ?? null,
      }),
    })
      .then(async (resp) => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const quoteData = await resp.json();
        this.setState({ quoteDetails: quoteData, loading: false });
      }, this.handleFetchError)
      .catch(this.handleFetchError);
  }

  firstNameEntered = (s) => {
    this.setState({ firstName: s });
  };

  lastNameEntered = (s) => {
    this.setState({ lastName: s });
  };

  emailEntered = (s) => {
    this.setState({ emailAddress: s });
  };

  phoneEntered = (s) => {
    this.setState({ phoneNumber: s });
  };

  handleEmailAddressValidation = (event) => {
    const isBlur = event.type === "blur";
    this.setState((prevState) => {
      let errors = { ...prevState.errors };

      if (!isBlur) {
        errors.emailAddress = false;
      } else {
        if (!prevState.emailAddress || !isEmailValid(prevState.emailAddress)) {
          errors.emailAddress = true;
        }
      }
      return { errors };
    });
  };

  handleNameFieldValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    this.setState((prevState) => {
      let errors = { ...prevState.errors };

      if (!isBlur) {
        errors[field] = false;
      } else {
        if (prevState[field] === "") {
          errors[field] = true;
        }
      }
      return { errors };
    });
  };

  handlePhoneNumberValidation = (event) => {
    const isBlur = event.type === "blur";
    this.setState((prevState) => {
      let errors = { ...prevState.errors };

      if (!isBlur) {
        errors.phoneNumber = false;
      } else {
        if (!prevState.phoneNumber || !isPhoneNumberValid(prevState.phoneNumber)) {
          errors.phoneNumber = true;
        }
      }
      return { errors };
    });
  };

  handleValidation = () => {
    const fields = { ...this.state };
    let errors = {};
    let formIsValid = true;

    if (fields["firstName"] === "") {
      errors["firstName"] = true;
      formIsValid = false;
    }

    if (fields["lastName"] === "") {
      errors["lastName"] = true;
      formIsValid = false;
    }

    if (fields["phoneNumber"] === "" || !isPhoneNumberValid(fields["phoneNumber"])) {
      errors["phoneNumber"] = true;
      formIsValid = false;
    }

    if (fields["emailAddress"] === "" || !isEmailValid(fields["emailAddress"])) {
      errors["emailAddress"] = true;
      formIsValid = false;
    }

    this.setState({ errors });
    return formIsValid;
  };

  continueImmediately = () => {
    if (this.handleValidation()) {
      this.props.onStepComplete({
        progress: "immediately",
        personalDetails: {
          firstName: this.state.firstName,
          lastName: this.state.lastName,
          emailAddress: this.state.emailAddress,
          phoneNumber: this.state.phoneNumber,
        },
      });
    }
  };

  render() {
    if (!!this.state.loading) {
      return null;
    }

    if (this.state.error) {
      return (
        <React.Fragment>
          <ScrollToTop />
          <Card title="Sorry, that didn’t work" removeTopPadding>
            {this.state.error}
          </Card>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <ScrollToTop />
        <Card title="Nearly there. Where should we send your quote?" removeTopPadding>
          <p>Add your details and we’ll:</p>
          <List bullet>
            <ListItem>Show your price on the next screen and email you the full quote</ListItem>
            <ListItem>Hold that price for 30 days</ListItem>
            <ListItem>Call you to answer any questions</ListItem>
          </List>

          <TextQuestion
            question="First name"
            autoComplete="given-name"
            value={this.state.firstName}
            valueEntered={this.firstNameEntered}
            validate={this.handleNameFieldValidation("firstName")}
            validationError={this.state.errors["firstName"] ? "Enter your first name" : null}
          />

          <TextQuestion
            question="Last name"
            autoComplete="family-name"
            value={this.state.lastName}
            valueEntered={this.lastNameEntered}
            validate={this.handleNameFieldValidation("lastName")}
            validationError={this.state.errors.lastName ? "Enter your last name" : null}
          />

          <TextQuestion
            question="Email"
            hint="We’ll email you a copy of your quote"
            inputMode="email"
            autoComplete="email"
            value={this.state.emailAddress}
            valueEntered={this.emailEntered}
            validate={this.handleEmailAddressValidation}
            validationError={this.state.errors.emailAddress ? "Enter an email address like name@example.com" : null}
          />

          <TextQuestion
            question="Phone number"
            hint="So one of our team can call and answer your questions. We won’t pass your number to anyone else."
            type="tel"
            autoComplete="tel"
            value={this.state.phoneNumber}
            valueEntered={this.phoneEntered}
            validate={this.handlePhoneNumberValidation}
            validationError={this.state.errors.phoneNumber ? "Enter a valid phone number" : null}
          />

          <Action onClick={this.continueImmediately}>Show my quote</Action>

          <StyledSmallPrint>
            No obligation, and nothing to pay now.{" "}
            <Link href="https://www.juno.legal/privacy" target="_blank" rel="noopener noreferrer">
              Privacy notice (opens in a new tab)
            </Link>
          </StyledSmallPrint>
        </Card>
        <TrustpilotBanner pageWidth={this.props.pageWidth} />
      </React.Fragment>
    );
  }
}

const StyledSmallPrint = styled.p`
  margin-top: 1em;
  font-size: var(--juno-font-size-body-sm);
  color: var(--juno-color-text-secondary);
`;

export default SaveQuoteForm;
